<template>
  <div class="payment-details-component">
    <div class="row">
      <div class="col-md-4">
        <div class="box box-primary">
          <div class="box-header">
            <h1 class="box-title">Credit</h1>
          </div>
          <div class="box-body">
            <h3>Account Balance</h3>
            <div class="credits" v-bind:class="{ 'red': credit < 0 }">
              Current account balance: <strong>{{ credit | currency_round_neg }}</strong>
            </div>
            Credits in your account balance may be used in the purchase of any
            reservation. If your account balance falls below $0 you will not be
            able to make any further reservations until this balance has been
            reconciled.
            <hr/>
            <h3>Add Funds</h3>
            You may place funds into your account balance by credit card below.
            The minimum custom top up amount is $5.00. If your balance is below
            -$2.00 you may choose to zero-off your balance.
            <hr class="hr-credit"/>
            <template v-if="credit <= -2">
              <div class="row">
                <div class="col-xs-6">
                  <span class="top-up">Add {{ zeroOffAmount | currency_round }}</span>
                </div>
                <div class="col-xs-6">
                  <span class="add-credit pull-right">
                    <button class="btn btn-default" type="button" v-on:click="addCreditModal('zero-off')">Add</button>
                  </span>
                </div>
              </div>
              <hr class="hr-credit"/>
            </template>
            <div class="row">
              <div class="col-xs-6">
                Enter amount<br/>
                <span class="top-up">$ <input type="text" v-model="enteredAmount" size="3" maxlength="3"/> .00</span> <br/>
            <div class="red" v-if="enteredAmount < 5">
              Minimum topup amount is $5.00
            </div>
            <div class="red" v-if="!isRegex">
              Must enter whole number
            </div>
          </div>
            <div class="col-xs-6">
              <span class="add-credit pull-right">
                <button class="btn btn-default" type="button" v-on:click="addCreditModal()" v-bind:disabled="enteredAmount < 5 || !isRegex">Add</button>
              </span>
            </div>
          </div>
          </div>
        </div>
        <div class="box box-primary">
          <div class="box-header">
            <h1 class="box-title">Pre-Authorization</h1>
          </div>
          <div class="box-body">
            <template v-if="account.HeldCharges.length == 0">
              None
            </template>
            <template v-else>
              <h4>
                {{ account.HeldCharges[0].cost | currency_round }} on hold
              </h4>
              <a v-link="{ path: '/preauth/' + account.HeldCharges[0].uuid }" class="btn-default btn btn-sm"><span class="fa fa-search"></span> View</a>
            </template>
          </div>
        </div>
        <div class="box box-primary">
          <div class="box-header">
            <h1 class="box-title">Cards</h1>
          </div>
          <div class="box-body">
            <template v-if="isMasterAccount && cards.length > 0">
              <p><strong>Business cards</strong> may be used by any drivers on your account.
              <strong>Personal cards</strong> are for your use only.</p>
            </template>
            <template v-for="card in cards">
              <template v-if="isMasterAccount">
                <span v-if="card.metadata.cardType == 'Business'" class="label label-primary">Business Card</span>
                <span v-if="card.metadata.cardType == 'Personal'" class="label label-warning">Personal Card</span>
                <a class="interactive-cursor" @click="changeCardType(card.metadata.cardType, card.id)">Change to {{ card.metadata.cardType == 'Business' ? 'Personal' : 'Business' }} Card</a>
              </template>
              <div class="box-item credit-card">
                <span v-if="card.metadata.nickName && !showAdd[$index]"> <span class="nickname">{{ card.metadata.nickName }}</span> <button class="btn btn-xs btn-info" @click="showAdd.$set($index, true)">Edit</button></span>
                <span v-if="!card.metadata.nickName && !showAdd[$index]"><button class="btn btn-xs btn-info" @click="showAdd.$set($index, true)">Add a card nickname</button></span>
                <span v-if="showAdd[$index]"><input size="13" type="text" v-model="cardNickName[$index]"/> <button class="btn btn-default btn-xs" @click="addCardName(cardNickName[$index], card.id)">Set</button><button class="btn btn-grey btn-xs" @click="showAdd.$set($index, false)">Back</button></span>
                <p class="numbers">**** **** **** {{ card.last4 }}</p>
                <p class="name">{{ card.name }}<br/>
                {{ card.address_line1 }}<br/>
                {{ card.address_state }} {{ card.address_country }} {{ card.address_zip }}</p>
                <p class="expiry">{{ card.exp_month }}/{{ card.exp_year }}</p>
                <span v-if="card.id == customer.default_source" class="label label-primary default-card">Default Card</span>
                <span v-if="card.id !== customer.default_source" class="btn btn-xs btn-info default-card" v-on:click="setDefaultCard(card.id)">Set Default Card</span>
                <span v-if="card.brand == 'Visa'" class="fa fa-lg fa-cc-visa"></span>
                <span v-if="card.brand == 'American Express'" class="fa fa-lg fa-cc-amex"></span>
                <span v-if="card.brand == 'MasterCard'" class="fa fa-lg fa-cc-mastercard"></span>
                <span v-if="card.brand == 'Discover'" class="fa fa-lg fa-cc-discover"></span>
                <span v-if="card.brand == 'JCB'" class="fa fa-lg fa-cc-jcb"></span>
                <span v-if="card.brand == 'Diners Club'" class="fa fa-lg fa-cc-diners-club"></span>
                <span v-if="card.brand == 'Unknown'" class="fa fa-lg fa-credit-card-alt"></span>
              </div>
            </template>
            <template v-if="cards.length == 0">
              <div class="alert alert-info">You currently do not have any cards on file</div>
            </template>
          </div>
          <div class="box-footer">
            <button class="btn btn-default" type="button" v-on:click="addCard()">Add Card</button>
          </div>
        </div>
      </div>
      <div class="col-md-8">
        <div class="box box-primary" v-if="isMasterAccount && (accountType.monthlyFee || accountType.annualFee)">
          <div class="box-header">
            <h1 class="box-title">Membership Fees</h1>
          </div>
          <div class="box-body">
            <h4>{{ accountType.name }} plan - {{ accountType.monthlyFee || accountType.annualFee | currency_round }} {{ billedAnnually ? 'per year' : 'per month (billed bi-annually)' }}</h4>
            <p><strong>Last payment received:</strong> {{ membershipLastPaid | short_date_format }}</p>
            <p :class="{'red': overdue}"><strong>Next payment due:</strong> {{ accountType.monthlyFee * 6 || accountType.annualFee | currency_round }} on {{ nextPaymentDue | short_date_format }}</p>
            <p class="alert alert-danger" v-if="overdue">
              Your recent automatic membership payment of
              {{ accountType.monthlyFee * 6 || accountType.annualFee | currency_round }}
              was declined and is now overdue. Your account is restricted and
              you will be unable to make any new reservations until this amount
              is paid.<br/><br/>
              <button class="btn btn-danger" @click="payMembershipFeeModal()">Pay Now</button>
            </p>
            <p v-else>
              Membership fees will automatically be deducted from your default credit card on their due date.
            </p>
          </div>
        </div>
        <div class="box box-primary">
          <div class="box-header">
            <h1 class="box-title">Billing History</h1>
          </div>
          <div class="box-body">
            <div class="nav-tabs-custom">
              <ul class="nav nav-tabs">
                <li class="active"><a href="#tab_1" data-toggle="tab" aria-expanded="true"><span class="fa fa-fw fa-credit-card"></span> Reservations</a></li>
                <li class=""><a href="#tab_7" data-toggle="tab" aria-expanded="false"><span class="fa fa-fw fa-usd"></span> Transactions</a></li>
                <li class=""><a href="#tab_3" data-toggle="tab" aria-expanded="false"><span class="fa fa-fw fa-table"></span> Monthly Invoices</a></li>
              </ul>
              <div class="tab-content container-fluid">
                <br/>
                <div class="tab-pane active fade in" id="tab_1">
                  <reservation-charges-tab></reservation-charges-tab>
                </div>
                <div class="tab-pane fade in" id="tab_7">
                  <object-select-input v-if="date.objects.length > 0" :field.sync="date"></object-select-input>
                  <br/><br/>
                  <responsive-table :table.sync="ledger"></responsive-table>
                </div>
                <div class="tab-pane fade in" id="tab_3">
                  <monthly-invoices-tab></monthly-invoices-tab>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-12">
        <p class="alert alert-info">Payments are processed by <a href="https://stripe.com/au/">Stripe</a>. You may view their <a href="https://stripe.com/au/privacy/">Privacy Policy here.</a></p>
      </div>
    </div>
  </div>
  <!-- Delete Modal -->
  <modal v-if="deleteCardModalActive" v-el:delete-modal>
    <template slot="header">
      Delete Card
    </template>
    <template slot="body">
      <div class="alert alert-info">Are you sure you want to delete the following credit card?</div>
      <div class="box-item credit-card">
        <p class="numbers">**** **** **** {{ selectedCard.last4 }}</p>
        <p class="name">{{ selectedCard.name }}<br/>
        {{ selectedCard.address_line1 }}<br/>
        {{ selectedCard.address_state }} {{ selectedCard.address_country }} {{ selectedCard.address_zip }}</p>
        <p class="expiry">{{ selectedCard.exp_month }}/{{ selectedCard.exp_year }}</p>
        <span v-if="selectedCard.brand == 'Visa'" class="fa fa-cc-visa"></span>
        <span v-if="selectedCard.brand == 'American Express'" class="fa fa-cc-amex"></span>
        <span v-if="selectedCard.brand == 'MasterCard'" class="fa fa-cc-mastercard"></span>
        <span v-if="selectedCard.brand == 'Discover'" class="fa fa-cc-discover"></span>
        <span v-if="selectedCard.brand == 'JCB'" class="fa fa-cc-jcb"></span>
        <span v-if="selectedCard.brand == 'Diners Club'" class="fa fa-cc-diners-club"></span>
        <span v-if="selectedCard.brand == 'Unknown'" class="fa fa-credit-card-alt"></span>
        <div class="actions">
        </div>
      </div>
    </template>
    <template slot="footer">
      <button type="button" class="btn btn-default pull-left" data-dismiss="modal">Cancel</button>
      <button class="btn btn-sm btn-danger" v-on:click="deleteCard(selectedCard.id)" data-dismiss="modal">Delete</button>
    </template>
  </modal>
  <!-- Add Credit Modal -->
  <modal v-if="addCreditModalActive" v-el:add-credit-modal>
    <template slot="header">
      Confirm Credit Topup
    </template>
    <template slot="body">
      <div class="alert alert-info">Are you sure you want to add {{ topUpAmount | currency_round }} to your account credits?</div>
      <template v-for="card in cards">
        <p class="callout callout-warning" v-if="card.id == customer.default_source">
        The default credit card you have on file will be charged. Card ending in <strong>{{ card.last4 }}</strong> with expiry <strong>{{ card.exp_month }} / {{ card.exp_year }}.</strong><br/>To change this, close this window.</p>
        </template>
        <div class="form-section-danger">Note: Account credits are non-refundable.</div>
    </template>
    <template slot="footer">
      <button type="button" class="btn btn-default pull-left" data-dismiss="modal">Cancel</button>
      <button class="btn btn-sm btn-info" v-on:click="addCredit()" data-dismiss="modal">Add</button>
    </template>
  </modal>
  <!-- Membership Fee Modal -->
  <modal v-if="membershipFeeModalActive" v-el:membership-fee-modal>
    <template slot="header">
      Pay Membership Fee
    </template>
    <template slot="body">
      <div class="alert alert-info">Your selected card will be charged {{ accountType.monthlyFee * 6 || accountType.annualFee | currency_round }}. Select a card and click 'Pay Now' to confirm payment.</div>
      <object-select-input v-if="selectCards.objects.length > 0" :field.sync="selectCards"></object-select-input>
      <p v-else class="alert alert-danger">You have no cards on file. Close this window and add a credit card to proceed.</p>
    </template>
    <template slot="footer">
      <button type="button" class="btn btn-default pull-left" data-dismiss="modal">Cancel</button>
      <button class="btn btn-sm btn-info" data-dismiss="modal" :disabled="!selectCards.value" @click="payMembershipFee()">Pay Now</button>
    </template>
  </modal>
  <!-- Success Modal -->
  <modal v-if="successModalActive" v-el:success-modal>
    <template slot="header">
      Success
    </template>
    <template slot="body">
      <p class="alert alert-success">
        Membership fee charged successfully.
      </p>
    </template>
  </modal>
  <!-- Error Modal -->
  <error-modal v-if="errorModalActive" v-bind:error-message="errorMessage"></error-modal>
</template>

<script>
import ErrorModal from '../modals/ErrorModal.vue'
import ObjectSelectInput from '../inputs/ObjectSelectInput.vue'
import ReservationChargesTab from '../tabs/ReservationChargesTab'
import Modal from '../Modal.vue'
import MonthlyInvoicesTab from '../tabs/MonthlyInvoicesTab'
import ResponsiveTable from '../displays/ResponsiveTable'

export default {
  name: 'PaymentDetails',
  components: {
    ErrorModal,
    ReservationChargesTab,
    Modal,
    MonthlyInvoicesTab,
    ObjectSelectInput,
    ResponsiveTable
  },
  data () {
    return {
      account: {
        HeldCharges: [],
        AccountType: {}
      },
      successModalActive: false,
      stripeHandler: false,
      addCreditModalActive: false,
      deleteCardModalActive: false,
      errorModalActive: false,
      errorMessage: '',
      cards: [],
      customer: {},
      selectedCard: {},
      credit: 0,
      enteredAmount: 5,
      isRegex: true,
      regex: /^[0-9]*$/,
      topUpAmount: 0,
      showAdd: [],
      cardNickName: [],
      isMasterAccount: false,
      activeReservations: 0,
      accountType: {},
      membershipLastPaid: null,
      billedAnnually: false,
      nextPaymentDue: null,
      overdue: false,
      membershipFeeModalActive: false,
      selectCards: {
        name: 'card',
        label: 'Card',
        value: '',
        iconName: 'icon',
        objectName: 'dropdownLabel',
        objects: [],
        valid: false,
        dirty: false
      },
      ledger: {
        title: 'All Transactions',
        count: 0,
        columns: [],
        rows: []
      },
      date: {
        name: 'date',
        label: 'Month',
        value: '',
        objects: [],
        valid: false,
        dirty: false,
        single_label: 'Month',
        objectName: 'uuid'
      },
      month: moment().format('MMM'),
      year: moment().format('YYYY')
    }
  },
  computed: {
    zeroOffAmount: function () {
      return Math.abs(this.credit)
    },
    currentMonth () {
      let selectedDate = moment(`${this.month} ${this.year}`, 'MMM YYYY').set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      let currentDate = moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      return selectedDate.isSame(currentDate)
    }
  },
  route: {
    activate () {
      this.$http.get('public/config', {}).then(({ data }, status, request) => {
        this.stripeHandler = StripeCheckout.configure({
          key: data.stripePublicKey,
          locale: 'auto',
          token: (token) => {
            this.$http.post('private/billing/addCard', { token }).then((response, status, request) => {
              this.listCards()
            })
          }
        })
        this.listCards()
      })
    },
    deactivate () {
      if (this.stripeHandler) {
        this.stripeHandler.close()
        $('iframe.stripe_checkout_app').remove()
      }
    }
  },
  ready () {
    this.getAccount()
    this.checkActiveReservations()
  },
  watch: {
    'enteredAmount': function (newVal, oldVal) {
      this.validateField(newVal)
    },
    'date.value': function (newVal, oldVal) {
      this.year = moment(newVal, 'MMM - YYYY').format('YYYY')
      this.month = moment(newVal, 'MMM - YYYY').format('MMM')
      this.getLedger()
    }
  },
  methods: {
    nextMonth () {
      let currentDate = moment(`${this.month} ${this.year}`, 'MMM YYYY')
      let nextMonth = currentDate.add(1, 'month')
      this.month = nextMonth.clone().format('MMM')
      this.year = nextMonth.clone().format('YYYY')
      this.getLedger()
    },
    prevMonth () {
      let currentDate = moment(`${this.month} ${this.year}`, 'MMM YYYY')
      let lastMonth = currentDate.subtract(1, 'month')
      this.month = lastMonth.clone().format('MMM')
      this.year = lastMonth.clone().format('YYYY')
      this.getLedger()
    },
    getLedger () {
      let params = {
        year: this.year,
        month: this.month
      }
      this.$http.get(`${this.$route.authType}/account-ledger`, { params }).then(({ data }, status, request) => {
        this.ledger = data.table
      })
    },
    payMembershipFee () {
      let amount = this.accountType.monthlyFee || this.accountType.annualFee
      this.$http.post('private/charge-membership-fee', {
        card: this.selectCards.value,
        amount,
        billedAnnually: this.billedAnnually
      }).then((response, status, request) => {
        if (response.data.result.error) {
          this.errorModal(response.data.result.error)
        } else {
          this.successModalActive = true
          this.getAccount()
          this.$refs.bulkCharges.getCharges()
          this.$nextTick(function () {
            $(this.$els.successModal).modal()
            $(this.$els.successModal).on('hidden.bs.modal', () => {
              this.successModalActive = false
            })
          })
        }
      })
    },
    payMembershipFeeModal () {
      this.membershipFeeModalActive = true
      this.$nextTick(function () {
        $(this.$els.membershipFeeModal).modal()
        $(this.$els.membershipFeeModal).on('hidden.bs.modal', () => {
          this.membershipFeeModalActive = false
        })
      })
    },
    addCredit () {
      let authType = this.$route.authType
      let amount = this.topUpAmount
      this.$http.post(`${authType}/add-credit`, { amount }).then((response, status, request) => {
        this.$refs.credits.getCreditCharges()
        this.getAccount()
      })
    },
    deleteCard (card) {
      this.$http.post('private/billing/deleteCard', { cardId: card }).then((response, status, request) => {
        this.listCards()
      })
    },
    changeCardType (type, cardId) {
      let cardType = ''
      if (type === 'Business') {
        cardType = 'Personal'
      } else {
        cardType = 'Business'
      }
      this.$http.post('private/billing/editCard', { type: cardType, cardId }).then((response, status, request) => {
        this.listCards()
      })
    },
    addCardName (name, cardId) {
      this.$http.post('private/billing/editCard', { name, cardId }).then((response, status, request) => {
        this.showAdd = []
        this.listCards()
      })
    },
    validateField (val) {
      this.isRegex = new RegExp(this.regex, 'gi').test(val)
    },
    addCreditModal (zeroOff) {
      if (this.cards.length === 0) {
        this.errorModal('You cannot add credit without an active credit card.')
      } else {
        if (zeroOff) this.topUpAmount = this.zeroOffAmount
        else this.topUpAmount = parseInt(this.enteredAmount)
        this.addCreditModalActive = true
        this.$nextTick(function () {
          $(this.$els.addCreditModal).modal()
          $(this.$els.addCreditModal).on('hidden.bs.modal', () => {
            this.addCreditModalActive = false
          })
        })
      }
    },
    submitForm () {
      return false
    },
    addCard () {
      this.stripeHandler.open({
        name: this.$parent.companyName,
        description: 'Payment Details',
        currency: 'AUD',
        billingAddress: true,
        panelLabel: 'Save Details',
        allowRememberMe: true
      })
    },
    deleteCardModal (card) {
      if (this.cards.length < 2 && this.credit < 0) {
        this.errorModal('You cannot delete your credit card while your account credit balance is negative.')
      } else if (this.activeReservations > 0) {
        this.errorModal('You cannot delete your credit cards with active reservations.')
      } else {
        this.selectedCard = card
        this.deleteCardModalActive = true
        this.$nextTick(function () {
          $(this.$els.deleteModal).modal()
          $(this.$els.deleteModal).on('hidden.bs.modal', () => {
            this.deleteCardModalActive = false
          })
        })
      }
    },
    errorModal (error) {
      this.errorMessage = error
      this.errorModalActive = true
      this.$nextTick(function () {
        $('#errorModal').modal()
        $('#errorModal').on('hidden.bs.modal', () => {
          this.errorModalActive = false
        })
      })
    },
    setDefaultCard (card) {
      this.$http.post('private/billing/setDefaultCard', { cardId: card }).then((response, status, request) => {
        this.listCards()
      })
    },
    listCards () {
      this.$http.get('private/billing/listCards', {}).then((response, status, request) => {
        this.cards = response.data.result.cards
        this.customer = response.data.result.customer
        this.cards.forEach((card) => {
          let cardName = `**** **** **** ${card.last4} - ${card.exp_month}/${card.exp_year}`
          if (card.metadata.nickName) {
            cardName = card.metadata.nickName
          }
          this.selectCards.objects.push({
            uuid: card.id,
            dropdownLabel: `${card.brand} - ${card.metadata.cardType}: ${cardName}`
          })
        })
      })
    },
    checkActiveReservations () {
      this.$http.get('private/check-active-reservations', {}).then((response, status, request) => {
        this.activeReservations = response.data
      })
    },
    getAccount () {
      this.$http.get('private/user/get-account', {}).then((response, status, request) => {
        this.credit = response.data.account.credits
        this.account = response.data.account
        this.isMasterAccount = response.data.account.isMasterAccount
        this.accountType = response.data.account.AccountType
        this.membershipLastPaid = response.data.account.membershipLastPaid
        this.billedAnnually = Boolean(parseInt(response.data.account.AccountType.annualFee))
        if (this.billedAnnually) {
          this.nextPaymentDue = moment(this.membershipLastPaid).add(1, 'year')
        } else {
          this.nextPaymentDue = moment(this.membershipLastPaid).add(6, 'months')
        }
        if (moment(this.nextPaymentDue).isBefore(moment())) {
          this.overdue = true
        } else {
          this.overdue = false
        }
        this.accountType.annualFee = parseInt(this.accountType.annualFee)
        this.accountType.monthlyFee = parseInt(this.accountType.monthlyFee)
        let registeredDate = moment(response.data.account.registeredDate).set({ date: 1, hour: 0, minute: 0, second: 0, millisecond: 0 })
        let november = moment('01/11/2016', 'DD-MM-YYYY').set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        let startDate = registeredDate
        if (registeredDate.isBefore(november)) startDate = november
        let endDate = moment().set({ date: 1, hour: 0, minute: 0, second: 0, millisecond: 0 })
        while (endDate.isSameOrAfter(startDate)) {
          this.date.objects.push({
            uuid: moment(endDate).clone().format('MMM - YYYY')
          })
          endDate.subtract(1, 'month')
        }
        this.year = moment(this.date.objects[0].uuid, 'MMM - YYYY').format('YYYY')
        this.month = moment(this.date.objects[0].uuid, 'MMM - YYYY').format('MMM')
        this.date.value = this.date.objects[0].uuid
        this.getLedger()
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// Billing.vue?551c9efe